import { useState } from 'react';
import { api, type SessionResult } from '../api/client';
import { LINK_EXPIRY_MS } from 'aliv-shared';

interface Props {
  sessions: SessionResult[];
  onRefresh: () => void;
}

function isLinkExpired(session: SessionResult): boolean {
  return (
    session.status === 'PENDING' &&
    Date.now() - new Date(session.createdAt).getTime() > LINK_EXPIRY_MS
  );
}

function statusBadge(session: SessionResult) {
  if (isLinkExpired(session)) return <span className="badge badge-expired">Link Expired</span>;
  if (session.status === 'PENDING') return <span className="badge badge-pending">Pending</span>;
  if (session.status === 'EXPIRED') return <span className="badge badge-expired">Expired</span>;
  if (session.status === 'SUCCEEDED' && session.passed)
    return <span className="badge badge-pass">Passed</span>;
  return <span className="badge badge-fail">Failed</span>;
}

function timeAgo(dateStr: string): string {
  const diffSec = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function SessionList({ sessions, onRefresh }: Props) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  function handleCopy(url: string, sessionId: string) {
    void navigator.clipboard.writeText(url);
    setCopiedId(sessionId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  async function handleDelete(sessionId: string) {
    try {
      await api.deleteSession(sessionId);
      onRefresh();
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  }

  if (sessions.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <div className="empty-title">No checks yet</div>
          <div className="empty-desc">Create your first liveness check above to get started.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card sessions-card">
      <div className="sessions-header">
        <h2>
          Sessions <span className="session-count">({sessions.length})</span>
        </h2>
        <button
          className="btn-icon"
          onClick={onRefresh}
          title="Refresh"
          aria-label="Refresh sessions"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
        </button>
      </div>

      <table>
        <caption className="sr-only">Verification sessions</caption>
        <thead>
          <tr>
            <th>Candidate</th>
            <th>Status</th>
            <th>Match Score</th>
            <th>Created</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.sessionId}>
              <td className="td-name">
                {s.candidateProfileUrl ? (
                  <a
                    href={s.candidateProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="candidate-link"
                  >
                    {s.candidateName}
                  </a>
                ) : (
                  s.candidateName
                )}
              </td>
              <td>{statusBadge(s)}</td>
              <td
                className={`td-confidence ${s.confidence !== undefined ? (s.passed ? 'pass' : 'fail') : 'na'}`}
              >
                {s.confidence !== undefined ? `${s.confidence.toFixed(1)}%` : '—'}
              </td>
              <td className="td-time" title={new Date(s.createdAt).toLocaleString()}>
                {timeAgo(s.createdAt)}
              </td>
              <td className="td-actions">
                {s.status === 'PENDING' && !isLinkExpired(s) && (
                  <button
                    className={`btn-copy ${copiedId === s.sessionId ? 'copied' : ''}`}
                    onClick={() => handleCopy(s.checkUrl, s.sessionId)}
                  >
                    {copiedId === s.sessionId ? 'Copied' : 'Copy Link'}
                  </button>
                )}
                {confirmingId === s.sessionId ? (
                  <span className="confirm-delete">
                    <span className="confirm-delete-label">Delete?</span>
                    <button
                      className="btn-confirm-delete"
                      onClick={() => {
                        setConfirmingId(null);
                        void handleDelete(s.sessionId);
                      }}
                    >
                      Yes
                    </button>
                    <button className="btn-cancel-delete" onClick={() => setConfirmingId(null)}>
                      No
                    </button>
                  </span>
                ) : (
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => setConfirmingId(s.sessionId)}
                    title="Delete"
                    aria-label="Delete session"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
